import { formatDate, formatMoney, type FinanceInvoice, type FinanceInvoiceItem } from '@/lib/finance';
import { buildInvoicePdf } from '@/lib/invoicePdf';
import type { CompanyProfile } from '@/lib/companyProfile';

// ─────────────────────────────────────────────────────────────
// Envoi / impression / partage des factures et devis.
// Le PDF est généré dans le navigateur puis :
//  - imprimé (nouvel onglet avec le lecteur PDF),
//  - partagé sur WhatsApp (partage natif du fichier si l'appareil
//    le permet, sinon message pré-rempli + PDF téléchargé),
//  - envoyé par e-mail (brouillon pré-rempli + PDF téléchargé).
// ─────────────────────────────────────────────────────────────

type ShareNavigator = Navigator & {
  canShare?: (data?: { files?: File[] }) => boolean;
  share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
};

export function invoicePdfBlob(
  invoice: FinanceInvoice,
  items: FinanceInvoiceItem[],
  profile: CompanyProfile,
): Blob {
  return buildInvoicePdf(invoice, items, profile).output('blob');
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function invoicePdfFilename(invoice: FinanceInvoice): string {
  const kind = invoice.doc_type === 'quote' ? 'Devis' : 'Facture';
  return `${kind}-${invoice.number}.pdf`;
}

/** Ouvre le PDF dans un nouvel onglet (lecteur PDF prêt à imprimer). */
export function printInvoice(
  invoice: FinanceInvoice,
  items: FinanceInvoiceItem[],
  profile: CompanyProfile,
): void {
  const blob = invoicePdfBlob(invoice, items, profile);
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) triggerDownload(blob, invoicePdfFilename(invoice));
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

/** Télécharge simplement le PDF. */
export function downloadInvoiceFile(
  invoice: FinanceInvoice,
  items: FinanceInvoiceItem[],
  profile: CompanyProfile,
): void {
  triggerDownload(invoicePdfBlob(invoice, items, profile), invoicePdfFilename(invoice));
}

export function buildInvoiceMessage(invoice: FinanceInvoice, profile: CompanyProfile): string {
  const isQuote = invoice.doc_type === 'quote';
  const label = isQuote ? 'devis' : 'facture';
  const parts = [
    `Bonjour${invoice.customer_name ? ` ${invoice.customer_name}` : ''},`,
    '',
    `Veuillez trouver ci-joint votre ${label} n° ${invoice.number} d'un montant de ${formatMoney(
      Number(invoice.total),
      invoice.currency || 'MAD',
    )}.`,
    `Date d'émission : ${formatDate(invoice.issue_date)}.`,
  ];
  if (invoice.due_date) {
    parts.push(`${isQuote ? 'Validité' : 'Échéance'} : ${formatDate(invoice.due_date)}.`);
  }
  if (profile.company_name) parts.push('', profile.company_name);
  return parts.join('\n');
}

function normalizePhone(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/[^\d]/g, '');
}

export type ShareResult = 'share' | 'whatsapp' | 'email' | 'downloaded';

/** Partage WhatsApp : fichier PDF via le partage natif, sinon message + PDF téléchargé. */
export async function shareInvoiceWhatsApp(
  invoice: FinanceInvoice,
  items: FinanceInvoiceItem[],
  profile: CompanyProfile,
): Promise<ShareResult> {
  const blob = invoicePdfBlob(invoice, items, profile);
  const file = new File([blob], invoicePdfFilename(invoice), { type: 'application/pdf' });
  const message = buildInvoiceMessage(invoice, profile);
  const nav = navigator as ShareNavigator;

  if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
    try {
      await nav.share({
        files: [file],
        title: `${invoice.doc_type === 'quote' ? 'Devis' : 'Facture'} ${invoice.number}`,
        text: message,
      });
      return 'share';
    } catch {
      /* l'utilisateur a annulé ou le partage a échoué : repli WhatsApp */
    }
  }

  triggerDownload(blob, invoicePdfFilename(invoice));
  const phone = normalizePhone(invoice.customer_phone);
  const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank');
  return 'whatsapp';
}

/** Prépare un e-mail avec le récapitulatif et télécharge le PDF à joindre. */
export function emailInvoice(
  invoice: FinanceInvoice,
  items: FinanceInvoiceItem[],
  profile: CompanyProfile,
): ShareResult {
  triggerDownload(invoicePdfBlob(invoice, items, profile), invoicePdfFilename(invoice));
  const isQuote = invoice.doc_type === 'quote';
  const subject = `${isQuote ? 'Devis' : 'Facture'} ${invoice.number}${
    profile.company_name ? ` — ${profile.company_name}` : ''
  }`;
  const body = buildInvoiceMessage(invoice, profile);
  const to = invoice.customer_email || '';
  window.open(`mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  return 'email';
}